import { useEffect, useRef, useState } from "react";
import { CLUB_GALLERY } from "@/data/clubMedia";

// Hand-pick a few of the best photos for the rounded photo card
const PHOTOS = [
  CLUB_GALLERY[0],
  CLUB_GALLERY[2],
  CLUB_GALLERY[5],
  CLUB_GALLERY[7],
  CLUB_GALLERY[3],
];

export default function About() {
  const [active, setActive] = useState(0);
  const total = PHOTOS.length;
  const lastInteractionRef = useRef(0);

  const goTo = (i) => {
    lastInteractionRef.current = Date.now();
    setActive(i);
  };

  // Auto-cycle every 3.8s, pause for 6s after manual interaction
  useEffect(() => {
    const id = setInterval(() => {
      if (Date.now() - lastInteractionRef.current < 6000) return;
      setActive((p) => (p + 1) % total);
    }, 3800);
    return () => clearInterval(id);
  }, [total]);

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-32 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20 items-center">
          {/* Left: text */}
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFB800] mb-8 flex items-center gap-3">
              <span className="h-px w-10 bg-[#FFB800]" />
              01 — Sobre el club
            </p>

            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-extrabold uppercase tracking-tight leading-[0.95]">
              El deporte<br />
              como <span className="italic font-light text-white/70">estilo de vida.</span>
            </h2>

            <div className="mt-12 max-w-xl text-white/70 text-lg leading-relaxed space-y-6">
              <p>
                Somos un club fundado en el año <span className="text-white">2015</span> en la ciudad de
                <span className="text-white"> Barcelona</span> con un objetivo claro: elegir el deporte como
                estilo de vida a través de actividades formativas y competitivas.
              </p>
              <p>
                Una década más tarde, seguimos creyendo en lo mismo: identidad,
                profesionalidad y comunidad. Un club hecho por y para su gente.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-3 max-w-md">
              {[
                { n: "10+", l: "Años de historia" },
                { n: "150+", l: "Socios activos" },
                { n: "5", l: "Títulos oficiales" },
              ].map((s, i) => (
                <div
                  key={i}
                  className="rounded-[1.75rem] border border-white/10 px-5 py-5 hover:border-[#FFB800]/50 transition-colors"
                >
                  <p className="font-display text-3xl md:text-4xl font-extrabold text-[#FFB800]">
                    {s.n}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-2">
                    {s.l}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: rounded photo card with auto-cycle */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] w-full max-w-[480px] mx-auto">
              {/* Decorative ring */}
              <div className="absolute -inset-3 rounded-[3.5rem] border border-white/8" aria-hidden />
              <div className="absolute -inset-1 rounded-[3rem] border border-[#FFB800]/15" aria-hidden />

              {/* Stack of cross-fading images */}
              <div className="relative w-full h-full rounded-[2.75rem] overflow-hidden bg-[#0A1121] shadow-[0_30px_80px_-30px_rgba(255,184,0,0.25)]">
                {PHOTOS.map((p, i) => (
                  <img
                    key={i}
                    src={p.src}
                    alt={p.caption}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                      i === active ? "opacity-100" : "opacity-0"
                    }`}
                    loading={i === 0 ? "eager" : "lazy"}
                    data-testid={`about-photo-${i}`}
                  />
                ))}
                <div className="absolute inset-0 bg-gradient-to-t from-[#030712]/60 via-transparent to-transparent pointer-events-none" />

                {/* Caption + counter */}
                <div className="absolute inset-0 p-7 flex flex-col justify-between pointer-events-none">
                  <span className="self-start font-mono text-[10px] uppercase tracking-[0.3em] text-white/85 bg-[#030712]/45 backdrop-blur-md rounded-full px-3 py-1.5 border border-white/10">
                    {String(active + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                  </span>
                  <p
                    key={active}
                    className="font-display text-2xl md:text-3xl font-extrabold uppercase tracking-tight reveal"
                  >
                    {PHOTOS[active].caption}
                  </p>
                </div>
              </div>

              {/* Floating circular badge */}
              <div
                className="absolute -top-6 -left-6 md:-left-10 w-24 h-24 md:w-28 md:h-28 rounded-full bg-[#FFB800] grid place-items-center text-[#030712] shadow-xl rotate-[-8deg] hover:rotate-0 transition-transform duration-700"
                data-testid="about-floating-badge"
              >
                <div className="text-center leading-tight">
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em]">desde</p>
                  <p className="font-display text-2xl font-extrabold">2015</p>
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em]">BCN</p>
                </div>
              </div>

              {/* Dots */}
              <div
                className="absolute -bottom-10 left-1/2 -translate-x-1/2 flex gap-2 z-20"
                data-testid="about-dots"
              >
                {PHOTOS.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => goTo(i)}
                    aria-label={`Foto ${i + 1}`}
                    data-testid={`about-dot-${i}`}
                    className={`h-3 px-1 -my-1 rounded-full transition-[width,background-color] duration-500 cursor-pointer ${
                      i === active
                        ? "w-10 bg-[#FFB800]"
                        : "w-3 bg-white/25 hover:bg-white/60"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
